export { resolveReplayTeamColors, DEFAULT_TEAM_COLORS as TEAM_COLORS } from "@Components/game/session-results/feedback/visualization/team-colors";
export type { ReplayTeamColors, TeamPalette } from "@Components/game/session-results/feedback/visualization/team-colors";

export interface ScreenCoordinate {
  x: number;
  y: number;
}

export interface PitchBounds {
  x: number;
  y: number;
  width: number;
  height: number;
  cornerRadius: number;
}

export interface GameCoordinate3D {
  x: number;
  y: number;
  z: number;
}

export const VISION_CONE_COLOR = 0xbdae09;

// Viewport dimensions for the visualization canvas/container
export const VIEWPORT_DIMENSIONS = {
  WIDTH: 1360,
  HEIGHT: 720,
  SCALE: 0.6,
  LINE_WIDTH: 10,
  Y_OFFSET: -25, // Vertical offset to move content upwards
} as const;

export const HEATMAP_SCALES = {
  WIDTH: 0.67,
  HEIGHT: 0.65,
} as const;

// Actual football pitch dimensions in screen units
export const PITCH_DIMENSIONS = {
  WIDTH: (105 / 68) * 525,
  HEIGHT: 525,
  CORNER_RADIUS: 10,
} as const;

// Goal positions on the visualization canvas
export const GOAL_ZONES = [
  { x: 38, y: VIEWPORT_DIMENSIONS.HEIGHT / 2 },
  { x: VIEWPORT_DIMENSIONS.WIDTH - 38, y: VIEWPORT_DIMENSIONS.HEIGHT / 2 },
] as const;

export const VISION_CONE_CONFIG = {
  RANGE: 600,
  ANGLE: (90 * Math.PI) / 180,
} as const;

export const PLAYER_SIZE = 24 * VIEWPORT_DIMENSIONS.SCALE;
export const SELECTED_PLAYER_SIZE = PLAYER_SIZE + 2;

export const ENGINE_PITCH_DIMENSIONS = {
  LENGTH: 105,
  WIDTH: 68,
} as const;

export const REPLAY_TO_ENGINE_TRANSFORM = {
  SCALE_X: 0.93,
  SCALE_Y: 0.9,
} as const;

export const VISUALIZATION_LAYOUT = {
  pitch: {
    x: VIEWPORT_DIMENSIONS.WIDTH / 2 - PITCH_DIMENSIONS.WIDTH / 2,
    y: VIEWPORT_DIMENSIONS.HEIGHT / 2 - PITCH_DIMENSIONS.HEIGHT / 2,
    width: PITCH_DIMENSIONS.WIDTH,
    height: PITCH_DIMENSIONS.HEIGHT,
    cornerRadius: PITCH_DIMENSIONS.CORNER_RADIUS,
  },
  playerLayerOrigin: {
    x: VIEWPORT_DIMENSIONS.WIDTH / 2 - PITCH_DIMENSIONS.WIDTH / 2,
    y: VIEWPORT_DIMENSIONS.HEIGHT / 2 - PITCH_DIMENSIONS.HEIGHT / 2,
  },
} as const satisfies {
  pitch: PitchBounds;
  playerLayerOrigin: ScreenCoordinate;
};

export const replayToPitchCoords = (replayX: number, replayY: number) => ({
  x: replayY * REPLAY_TO_ENGINE_TRANSFORM.SCALE_X + ENGINE_PITCH_DIMENSIONS.LENGTH / 2,
  y: replayX * REPLAY_TO_ENGINE_TRANSFORM.SCALE_Y + ENGINE_PITCH_DIMENSIONS.WIDTH / 2,
});

export const pitchToScreenCoords = (pitchX: number, pitchY: number) => ({
  x: ((ENGINE_PITCH_DIMENSIONS.LENGTH - pitchX) / ENGINE_PITCH_DIMENSIONS.LENGTH) * PITCH_DIMENSIONS.WIDTH,
  y: ((ENGINE_PITCH_DIMENSIONS.WIDTH - pitchY) / ENGINE_PITCH_DIMENSIONS.WIDTH) * PITCH_DIMENSIONS.HEIGHT,
});

export const gameToScreenCoords = (gameX: number, gameY: number) => {
  const pitchCoords = replayToPitchCoords(gameX, gameY);
  return pitchToScreenCoords(pitchCoords.x, pitchCoords.y);
};

export const gameToStageCoords = (gameX: number, gameY: number) => {
  const position = gameToScreenCoords(gameX, gameY);

  return {
    x: VISUALIZATION_LAYOUT.playerLayerOrigin.x + position.x,
    y: VISUALIZATION_LAYOUT.playerLayerOrigin.y + position.y,
  };
};

export const gameToPitchCoords = (gameX: number, gameY: number) => {
  const position = gameToStageCoords(gameX, gameY);

  return {
    x: position.x - VISUALIZATION_LAYOUT.pitch.x,
    y: position.y - VISUALIZATION_LAYOUT.pitch.y,
  };
};

export const getBallPosition = (replayData: any, currentTime: number) => {
  const ballPositions = replayData?.ball?.position;
  const ballRotations = replayData?.ball?.rotation;

  if (!ballPositions || ballPositions.length === 0) {
    return { ballPosition: null, ballRotation: null };
  }

  let currentPos = ballPositions[0];
  let currentRot = ballRotations?.[0]?.angle ?? 0;

  for (let i = 0; i < ballPositions.length - 1; i++) {
    if (currentTime >= ballPositions[i].time && currentTime <= ballPositions[i + 1].time) {
      const pos1 = ballPositions[i];
      const pos2 = ballPositions[i + 1];
      const progress = (currentTime - pos1.time) / (pos2.time - pos1.time);

      currentPos = {
        x: pos1.x + (pos2.x - pos1.x) * progress,
        y: pos1.y + (pos2.y - pos1.y) * progress,
        z: pos1.z + (pos2.z - pos1.z) * progress,
      };

      const rot1 = ballRotations?.[i]?.angle ?? currentRot;
      const rot2 = ballRotations?.[i + 1]?.angle ?? rot1;

      currentRot = rot1 + (rot2 - rot1) * progress;

      break;
    } else if (currentTime > ballPositions[i].time) {
      currentPos = ballPositions[i];
    }
  }

  let pos2D = gameToScreenCoords(currentPos.x, currentPos.y);
  let finalPos = { x: pos2D.x, y: pos2D.y, z: currentPos.z };

  return { ballPosition: finalPos, ballRotation: currentRot };
};
